import React from 'react';
import { pick } from 'lodash';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
    FormControl,
    FormControlLabel,
    FormLabel,
    Grid,
    Radio,
    RadioGroup,
    Typography
} from '@mui/material';

import {
    useChangeable,
    FullForm,
    AutocompleteField,
    BoolField,
    TextField,
    RangeField
} from '../CommonComponents/Form';
import SelectField from '../CommonComponents/Form/fields/SelectField';

import { SectionDivider } from '../CommonComponents';
import ShapeAreaEditor from '../CommonComponents/GoogleMap/ShapeAreaEditor';
import type { AreaShape } from '../CommonComponents/GoogleMap/ShapeAreaEditor';

import {
    createWatchlist,
    getWatchlist,
    updateWatchlist,
    deleteWatchlist
} from '../../api-admin/watchlists';
import { getUser } from '../../api-admin/users';
import { getTenant, searchTenants } from '../../api-admin/tenants';
import toast from '../../utils/toast';
import { fetchGroupsByTenantId, selectGroupsByTenantId } from '../../redux/actions/groups';
import { useDispatch, useSelector } from '../../redux/hooks';
import { defaultMarketFilters, marketFilterLimits } from '../../redux/slices/market';
import { GROUP_AREA_LABEL, formatSource, matchModeLabels } from '../../utils/parsing';
import {
    circleToHexagonPoints, polygonToCircle, routeColor, squareRoutePoints
} from '../../utils/tenantRoutes';
import type { TenantRoute, TenantRoutePoint, WatchlistType } from '../../types';
import Loading from '../CommonComponents/Fetch/Loading';

// Area, match mode and notification mode as in the webapp's WatchlistForm (LL-415). The admin API
// applies the same validation as the user API, on behalf of the watchlist's user.
type AreaMode = 'none' | 'route' | 'own';
type NotificationMode = 'off' | 'instant' | 'daily';

const DEFAULT_RADIUS_M = 50000;
const POLYGON_MAP_ROUTE_ID = 1;

const AREA_MODE_OPTIONS: { value: AreaMode, label: string }[] = [
    { value: 'none', label: 'Hela landet' },
    { value: 'route', label: 'Företagets rutt' },
    { value: 'own', label: 'Eget område' }
];

const NOTIFICATION_MODE_OPTIONS: { value: NotificationMode, label: string }[] = [
    { value: 'off', label: 'Av (pausad)' },
    { value: 'daily', label: 'Dagligen' },
    { value: 'instant', label: 'Direkt' }
];

function toAreaFormState(w: Partial<WatchlistType>) {
    const detachedRoute = !w.routeId && !w.areaType && (w.source === 'route' || w.source === 'residence');
    const shape: any = w.areaShape;
    let areaMode: AreaMode = 'none';
    if (w.routeId || detachedRoute) {
        areaMode = 'route';
    } else if (w.areaType) {
        areaMode = 'own';
    }
    return {
        areaMode,
        areaShapeMode: (w.areaType ?? 'polygon') as AreaShape,
        routeId: w.routeId ?? null,
        matchMode: w.matchMode ?? 'within',
        polygonPoints: w.areaType === 'polygon' ? shape.points : null,
        circleCenter: w.areaType === 'circle' ? shape.center : null,
        circleRadiusM: w.areaType === 'circle' ? shape.radius : DEFAULT_RADIUS_M
    };
}

/** Paused is "off"; both channels on (group watchlists) shows as "instant"; active without a channel is null. */
function getNotificationMode(w: { isActive?: boolean, instantNotifications?: boolean, dailyNotifications?: boolean }) {
    if (!w.isActive) return 'off';
    if (w.instantNotifications) return 'instant';
    return w.dailyNotifications ? 'daily' : null;
}

/** Av keeps the channel flags; Direkt/Dagligen = active with exactly that channel. */
function toNotificationFields(entity: any, mode: NotificationMode | null) {
    if (mode === 'off' || mode === null) {
        return {
            isActive: mode === null,
            instantNotifications: !!entity.instantNotifications,
            dailyNotifications: !!entity.dailyNotifications
        };
    }
    return {
        isActive: true,
        instantNotifications: mode === 'instant',
        dailyNotifications: mode === 'daily'
    };
}

function toArea(entity: any) {
    if (entity.areaMode === 'own' && entity.areaShapeMode === 'polygon' && entity.polygonPoints) {
        return { type: 'polygon', shape: { points: entity.polygonPoints } };
    }
    if (entity.areaMode === 'own' && entity.areaShapeMode === 'circle' && entity.circleCenter) {
        return { type: 'circle', shape: { center: entity.circleCenter, radius: Math.round(entity.circleRadiusM) } };
    }
    return null;
}

/** The area is always sent: routeId + area both null is the deliberate "whole country". */
function toRequestBody(entity: any) {
    return {
        ...toNotificationFields(entity, entity.notificationMode),
        ...pick(entity, [
            'userId', 'name', 'isJob', 'isCapacity', 'takesOffer', 'groupId', 'hasListPrice', 'matchMode',
            'minGrossWeight', 'maxGrossWeight', 'minChargeableWeight', 'maxChargeableWeight', 'minVolume', 'maxVolume',
            'minLoadingMeters', 'maxLoadingMeters', 'minPalletPlaces', 'maxPalletPlaces', 'minPallets', 'maxPallets'
        ]),
        publisherTenantId: entity.publisherTenantId ?? null,
        routeId: entity.areaMode === 'route' ? entity.routeId : null,
        area: toArea(entity)
    };
}

function getAreaError(entity: any): string | null {
    if (entity.areaMode === 'route' && !entity.routeId) {
        return 'Välj en av företagets rutter, eller välj ett annat områdesalternativ.';
    }
    if (entity.areaMode === 'own' && entity.areaShapeMode === 'polygon' && (entity.polygonPoints?.length ?? 0) < 3) {
        return 'Rita ett område på kartan.';
    }
    if (entity.areaMode === 'own' && entity.areaShapeMode === 'circle' && !entity.circleCenter) {
        return 'Välj en plats för cirkeln.';
    }
    return null;
}

/** A shape not set yet is seeded from the other one (or the tenant's home). */
function getShapeDefaults(entity: any, shape: AreaShape, home: { lat?: number | null, lng?: number | null }) {
    if (shape === 'polygon' && !entity.polygonPoints) {
        return {
            polygonPoints: entity.circleCenter
                ? circleToHexagonPoints(entity.circleCenter, entity.circleRadiusM)
                : squareRoutePoints(home.lat, home.lng)
        };
    }
    if (shape === 'circle' && !entity.circleCenter && entity.polygonPoints?.length) {
        const { center, radiusM } = polygonToCircle(entity.polygonPoints);
        return { circleCenter: center, circleRadiusM: radiusM };
    }
    return {};
}

export default function WatchlistForm() {
    const { id, userId } = useParams();
    const [entity, onChange] = useChangeable({
        ...defaultMarketFilters,
        isCapacity: false,
        ...toAreaFormState({}),
        notificationMode: 'instant',
        instantNotifications: true,
        dailyNotifications: false,
        publisherTenantId: null,
        publisherTenantName: ''
    });
    const [tenant, setTenant] = React.useState<{ lat?: number | null, lng?: number | null, routes: TenantRoute[] }>({
        routes: []
    });
    const navigate = useNavigate();
    const location = useLocation();
    // Where saving, creating and deleting end up: the user's tab when opened from there
    const returnTo = (location.state as { returnTo?: string } | null)?.returnTo ?? '/watchlists';
    const dispatch = useDispatch();
    const groups = useSelector(selectGroupsByTenantId(entity.tenantId));
    const hasFetchedRef = React.useRef(false);

    const getDataForEditingWatchlist = React.useCallback(async () => {
        try {
            if (!id) {
                throw new Error('Could not retrieve watchlist, missing id');
            }
            const watchlist = await getWatchlist(id);
            if (!watchlist) {
                throw new Error(`Could not retrieve watchlist with id: ${id}`);
            }
            hasFetchedRef.current = true;
            onChange({
                ...watchlist,
                ...toAreaFormState(watchlist),
                notificationMode: getNotificationMode(watchlist)
            });
        } catch (e: any) {
            toast(e, 'Kunde inte hämta bevakningen, försök igen', false);
            navigate(-1);
        }
    }, [id, navigate, onChange]);

    const getDataForCreatingWatchlist = React.useCallback(async () => {
        try {
            if (!userId) {
                throw new Error('Could not retrieve user, missing id');
            }
            const {
                tenantId,
                tenantName,
                firstName,
                lastName
            } = await getUser(userId);
            if (!tenantId || !tenantName || (!firstName && !lastName)) {
                throw new Error(`Could not retrieve data for user with id: ${userId}`);
            }
            const userName = [firstName, lastName].filter(Boolean).join(' ');
            hasFetchedRef.current = true;
            onChange({
                tenantId,
                tenantName,
                userName,
                userId
            });
        } catch (e: any) {
            toast('Kunde inte hämta användarens uppgifter, försök igen', undefined, false);
            navigate(-1);
        }
    }, [userId, navigate, onChange]);

    React.useEffect(() => {
        if (id && !userId && !hasFetchedRef.current) {
            getDataForEditingWatchlist();
        } else if (userId && !id && !hasFetchedRef.current) {
            getDataForCreatingWatchlist();
        }
    }, [userId, id, getDataForEditingWatchlist, getDataForCreatingWatchlist]);

    React.useEffect(() => {
        if (!entity.tenantId) return;
        getTenant(entity.tenantId)
            .then((t: any) => setTenant({ lat: t?.lat, lng: t?.lng, routes: t?.routes ?? [] }))
            .catch((e: any) => toast(e, 'Kunde inte hämta företagets rutter', false));
    }, [entity.tenantId]);

    const setAreaMode = React.useCallback((areaMode: AreaMode) => {
        onChange({
            areaMode,
            ...(areaMode === 'own' ? getShapeDefaults(entity, entity.areaShapeMode, tenant) : {})
        });
    }, [entity, tenant, onChange]);

    const setAreaShape = React.useCallback((areaShapeMode: AreaShape) => {
        onChange({ areaShapeMode, ...getShapeDefaults(entity, areaShapeMode, tenant) });
    }, [entity, tenant, onChange]);

    const onCircleChange = React.useCallback((circleCenter: TenantRoutePoint, circleRadiusM: number) => {
        onChange({ circleCenter, circleRadiusM });
    }, [onChange]);

    const onPolygonChange = React.useCallback((_id: number, polygonPoints: TenantRoutePoint[]) => {
        onChange({ polygonPoints });
    }, [onChange]);

    const selectedRoute = tenant.routes.find((r) => r.id === entity.routeId);
    const mapRoutes: TenantRoute[] = React.useMemo(() => {
        if (entity.areaMode === 'own' && entity.areaShapeMode === 'polygon' && entity.polygonPoints) {
            return [{ id: POLYGON_MAP_ROUTE_ID, name: 'Område', points: entity.polygonPoints }];
        }
        return entity.areaMode === 'route' && selectedRoute ? [selectedRoute] : [];
    }, [entity.areaMode, entity.areaShapeMode, entity.polygonPoints, selectedRoute]);

    const fetchGroups = React.useCallback(() => {
        if (entity.tenantId) {
            dispatch(fetchGroupsByTenantId(entity.tenantId));
        }
    }, [entity.tenantId, dispatch]);

    const onSubmit = React.useCallback(async () => {
        const areaError = getAreaError(entity);
        if (areaError) {
            toast(areaError, undefined, false);
            return;
        }
        const body = toRequestBody(entity);
        try {
            if (id) {
                await updateWatchlist(id, body);
                toast('Bevakning uppdaterad', undefined, true);
            } else {
                await createWatchlist(body);
                toast('Bevakning skapad', undefined, true);
            }
            navigate(returnTo, { replace: true });
        } catch (e: any) {
            toast(e, `Kunde inte ${id ? 'uppdatera' : 'skapa'} bevakningen, försök igen`, false);
        }
    }, [id, entity, navigate, returnTo]);

    const onDelete = React.useCallback(async () => {
        try {
            if (id) {
                await deleteWatchlist(id);
                toast('Bevakning borttagen', undefined, true);
                navigate(returnTo, { replace: true });
            }
        } catch (e: any) {
            toast(e, 'Kunde inte ta bort bevakningen, försök igen', false);
        }
    }, [id, navigate, returnTo]);

    if (id && !hasFetchedRef.current) {
        return <Loading name="uppdraget" />;
    }

    return (
        <FullForm
            title={`${entity.id ? 'Redigera' : 'Skapa'} bevakning`}
            entity={entity}
            onSubmit={onSubmit}
            onDelete={entity.id ? onDelete : undefined}
            showBottomSave
        >
            <Grid container spacing={2} justifyContent="space-between" sx={{ mb: 2 }}>
                <Grid item xs={12} lg={6}>

                    <SectionDivider label="Grundläggande" marginTop={0} />

                    <Grid container spacing={2} justifyContent="space-between" sx={{ mb: 2 }}>
                        <Grid item xs={12}>
                            <TextField
                                name="name"
                                label="Namn på bevakning"
                                value={entity.name}
                                onChange={onChange}
                                required
                            />
                        </Grid>
                    </Grid>
                    {!id && (
                        <>
                            <Grid container spacing={2} justifyContent="space-between" sx={{ mb: 2 }}>
                                <Grid item xs={12}>
                                    <TextField
                                        name="tenantName"
                                        label="Företag"
                                        value={entity.tenantName}
                                        onChange={onChange}
                                        disabled
                                    />
                                </Grid>
                            </Grid>
                            <Grid container spacing={2} justifyContent="space-between" sx={{ mb: 2 }}>
                                <Grid item xs={12}>
                                    <TextField
                                        name="userName"
                                        label="Användare"
                                        value={entity.userName}
                                        onChange={onChange}
                                        disabled
                                    />
                                </Grid>
                            </Grid>
                        </>
                    )}
                    <Grid container spacing={0} justifyContent="space-between" sx={{ mb: 2 }}>
                        <Grid item xs={12} md={6}>
                            <BoolField
                                name="isJob"
                                label="Uppdrag"
                                value={entity.isJob}
                                onChange={({ isJob }) => onChange(isJob ? { isJob } : { isJob, takesOffer: false })}
                            />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <BoolField
                                name="isCapacity"
                                label="Kapacitet"
                                value={entity.isCapacity}
                                onChange={onChange}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <BoolField
                                name="externalAssignments"
                                label="Externa uppdrag"
                                helperText="Uppdrag från externa källor. Här kan du inte lämna intresseanmälan."
                                value={entity.isJob && !entity.takesOffer}
                                disabled={!entity.isJob}
                                onChange={({ externalAssignments }) => onChange({ takesOffer: !externalAssignments })}
                            />
                        </Grid>
                    </Grid>

                    <SectionDivider label="Område" marginBottom={1} />

                    <Grid container spacing={2} sx={{ mb: 2 }}>
                        <Grid item xs={12}>
                            <FormControl>
                                <FormLabel id="watchlist-area-mode">Bevaka</FormLabel>
                                <RadioGroup
                                    aria-labelledby="watchlist-area-mode"
                                    value={entity.areaMode}
                                    onChange={(e) => setAreaMode(e.target.value as AreaMode)}
                                >
                                    {AREA_MODE_OPTIONS.map((o) => (
                                        <FormControlLabel
                                            key={o.value}
                                            value={o.value}
                                            control={<Radio size="small" />}
                                            label={o.value === 'none' && entity.groupId ? GROUP_AREA_LABEL : o.label}
                                        />
                                    ))}
                                </RadioGroup>
                            </FormControl>
                        </Grid>
                        {entity.areaMode === 'route' && (
                            <Grid item xs={12}>
                                <SelectField
                                    name="routeId"
                                    label="Rutt"
                                    required
                                    value={entity.routeId ?? ''}
                                    options={tenant.routes.map((r) => ({ value: r.id, label: r.name }))}
                                    helperText={tenant.routes.length === 0 ? 'Företaget har inga rutter.' : ''}
                                    onChange={onChange}
                                />
                            </Grid>
                        )}
                        <Grid item xs={12}>
                            <ShapeAreaEditor
                                shape={entity.areaMode === 'own' && entity.areaShapeMode === 'circle' ? 'circle' : 'polygon'}
                                onShapeChange={setAreaShape}
                                showToggle={entity.areaMode === 'own'}
                                routes={mapRoutes}
                                selectedId={entity.areaMode === 'own' ? POLYGON_MAP_ROUTE_ID : null}
                                fitToId={entity.areaMode === 'own' ? POLYGON_MAP_ROUTE_ID : entity.routeId}
                                onPointsChange={onPolygonChange}
                                circleCenter={entity.circleCenter}
                                circleRadiusM={entity.circleRadiusM}
                                onCircleChange={onCircleChange}
                                otherRoutes={tenant.routes.map((route, index) => ({ ...route, color: routeColor(index) }))}
                                height="clamp(260px, 40vh, 480px)"
                            />
                        </Grid>
                        {entity.areaMode !== 'none' && (
                            <Grid item xs={12}>
                                <FormControl>
                                    <FormLabel id="watchlist-match-mode">Matchning</FormLabel>
                                    <RadioGroup
                                        aria-labelledby="watchlist-match-mode"
                                        value={entity.matchMode}
                                        onChange={(e) => onChange({ matchMode: e.target.value })}
                                    >
                                        {(['within', 'touches'] as const).map((value) => (
                                            <FormControlLabel
                                                key={value}
                                                value={value}
                                                control={<Radio size="small" />}
                                                label={matchModeLabels[value]}
                                            />
                                        ))}
                                    </RadioGroup>
                                </FormControl>
                            </Grid>
                        )}
                        {id && (
                            <Grid item xs={12}>
                                <Typography variant="caption">
                                    {`Källa: ${formatSource(entity as WatchlistType)}. Metadata hämtas från företagets profil.`}
                                </Typography>
                            </Grid>
                        )}
                    </Grid>

                    <SectionDivider label="Gods" marginTop={4} marginBottom={1} />

                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <RangeField
                                name="grossWeight"
                                label="Totalvikt [kg]"
                                minValue={marketFilterLimits.minGrossWeight}
                                maxValue={marketFilterLimits.maxGrossWeight}
                                lowValue={entity.minGrossWeight || marketFilterLimits.minGrossWeight}
                                highValue={entity.maxGrossWeight || marketFilterLimits.maxGrossWeight}
                                onChange={([minGrossWeight, maxGrossWeight]) => {
                                    onChange({ minGrossWeight, maxGrossWeight });
                                }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <RangeField
                                name="chargeableWeight"
                                label="FDR-vikt [kg]"
                                minValue={marketFilterLimits.minChargeableWeight}
                                maxValue={marketFilterLimits.maxChargeableWeight}
                                lowValue={entity.minChargeableWeight || marketFilterLimits.minChargeableWeight}
                                highValue={entity.maxChargeableWeight || marketFilterLimits.maxChargeableWeight}
                                onChange={([minChargeableWeight, maxChargeableWeight]) => {
                                    onChange({ minChargeableWeight, maxChargeableWeight });
                                }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <RangeField
                                name="loadingMeters"
                                label="Flakmeter"
                                minValue={marketFilterLimits.minLoadingMeters}
                                maxValue={marketFilterLimits.maxLoadingMeters}
                                lowValue={entity.minLoadingMeters || marketFilterLimits.minLoadingMeters}
                                highValue={entity.maxLoadingMeters || marketFilterLimits.maxLoadingMeters}
                                onChange={([minLoadingMeters, maxLoadingMeters]) => {
                                    onChange({ minLoadingMeters, maxLoadingMeters });
                                }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <RangeField
                                name="volume"
                                label="Volym [m³]"
                                minValue={marketFilterLimits.minVolume}
                                maxValue={marketFilterLimits.maxVolume}
                                lowValue={entity.minVolume || marketFilterLimits.minVolume}
                                highValue={entity.maxVolume || marketFilterLimits.maxVolume}
                                onChange={([minVolume, maxVolume]) => {
                                    onChange({ minVolume, maxVolume });
                                }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <RangeField
                                name="palletPlaces"
                                label="Pallplatser"
                                minValue={marketFilterLimits.minPalletPlaces}
                                maxValue={marketFilterLimits.maxPalletPlaces}
                                lowValue={entity.minPalletPlaces || marketFilterLimits.minPalletPlaces}
                                highValue={entity.maxPalletPlaces || marketFilterLimits.maxPalletPlaces}
                                onChange={([minPalletPlaces, maxPalletPlaces]) => {
                                    onChange({ minPalletPlaces, maxPalletPlaces });
                                }}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <RangeField
                                name="pallets"
                                label="Pallar"
                                minValue={marketFilterLimits.minPallets}
                                maxValue={marketFilterLimits.maxPallets}
                                lowValue={entity.minPallets || marketFilterLimits.minPallets}
                                highValue={entity.maxPallets || marketFilterLimits.maxPallets}
                                onChange={([minPallets, maxPallets]) => {
                                    onChange({ minPallets, maxPallets });
                                }}
                            />
                        </Grid>
                    </Grid>
                </Grid>
                <Grid item xs={12} lg={6}>
                    <SectionDivider label="Notiser" marginTop={0} />

                    <Grid container spacing={0} justifyContent="space-between" sx={{ mb: 2 }}>
                        <Grid item xs={12}>
                            <RadioGroup
                                row
                                aria-label="Notiser för bevakningen"
                                value={entity.notificationMode ?? ''}
                                onChange={(e) => onChange({ notificationMode: e.target.value })}
                            >
                                {NOTIFICATION_MODE_OPTIONS.map((o) => (
                                    <FormControlLabel
                                        key={o.value}
                                        value={o.value}
                                        control={<Radio size="small" />}
                                        label={o.label}
                                    />
                                ))}
                            </RadioGroup>
                            {entity.notificationMode === null && (
                                <Typography variant="caption">
                                    Aktiv men varken direkt- eller dagliga notiser är valda.
                                </Typography>
                            )}
                        </Grid>
                    </Grid>

                    <SectionDivider label="Publicering" marginTop={4} marginBottom={1} />

                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <AutocompleteField<{ id: number, name: string, ownerTenantName?: string }>
                                label="Endast i grupp"
                                optionIdKey="id"
                                defaultOption={!entity.groupId ? null : {
                                    id: entity.groupId,
                                    name: entity.groupName
                                }}
                                fetchOptions={fetchGroups}
                                options={groups.filter(
                                    (o: { isOwner: boolean; isAccepted: boolean; }) => (o.isOwner || o.isAccepted)
                                )}
                                getOptionName={(o) => o.name}
                                getOptionDescription={(o) => o.ownerTenantName || ''}
                                onChange={(group) => onChange({
                                    groupId: group ? group.id : null,
                                    groupName: group ? group.name : null
                                })}
                                fetchOnlyOnce
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <AutocompleteField<{ id: number, name: string, orgNumber?: string }>
                                label="Avsändare (valfritt)"
                                helperText="Bara uppdrag där avsändaren är synlig matchar."
                                optionIdKey="id"
                                defaultOption={!entity.publisherTenantId ? null : {
                                    id: entity.publisherTenantId,
                                    name: entity.publisherTenantName || `Företag ${entity.publisherTenantId}`
                                }}
                                fetchOptions={(query) => searchTenants(query || '')}
                                minQueryLength={3}
                                getOptionName={(o) => o.name}
                                getOptionDescription={(o) => o.orgNumber || ''}
                                onChange={(company) => onChange({
                                    publisherTenantId: company ? company.id : null,
                                    publisherTenantName: company ? company.name : ''
                                })}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <BoolField
                                name="hasListPrice"
                                label="Endast uppdrag med riktpris"
                                value={entity.hasListPrice}
                                onChange={({ hasListPrice }) => onChange({ hasListPrice })}
                            />
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        </FullForm>
    );
}
